import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { gsap } from "gsap";
import apiService from "./apiService";
import Navbar from "./components/Navbar";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import FloatingRegistrationButtons from "./components/FloatingRegistrationButtons";

// Eagerly loaded — always visible on every page
const PremiumPopup = lazy(() => import("./pages/Popup"));

// Pages — loaded only when route is visited
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const FounderDirectors = lazy(() => import("./pages/gtf/FounderDirectors"));
const RajendraShende = lazy(() => import("./pages/gtf/founders/RajendraShende"));
const SandeepNarang = lazy(() => import("./pages/gtf/founders/SandeepNarang"));
const OurAssociatesPage = lazy(() => import("./pages/gtf/OurAssociatesPage"));
const SCCNMentorsPage = lazy(() => import("./pages/sccn/Mentors"));
const SCCNAdvisorsPage = lazy(() => import("./pages/sccn/Advisors"));
const SCCNExpertsPage = lazy(() => import("./pages/sccn/Experts"));
const SCCNTeamPage = lazy(() => import("./pages/sccn/Team"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const SignIn = lazy(() => import("./pages/SignIn"));
const Register = lazy(() => import("./pages/Register"));
const UniversityRegister = lazy(() => import("./pages/UniversityRegister"));
const StudentRegister = lazy(() => import("./pages/StudentRegister"));
const UniversityDashboard = lazy(() => import("./pages/UniversityDashboard"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const StudentProfile = lazy(() => import("./pages/StudentProfile"));
const UniversityProfile = lazy(() => import("./pages/UniversityProfile"));
const BlogsPage = lazy(() => import("./pages/BlogsPage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));
const NewsletterDetailPage = lazy(() => import("./pages/NewsletterDetailPage"));
const SimplySmartPage = lazy(() => import("./pages/SimplySmartPage"));
const GloballySmartPage = lazy(() => import("./pages/GloballySmartPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const PastEventsPage = lazy(() => import("./pages/PastEventsPage"));
const EventDetailPage = lazy(() => import("./pages/EventDetailPage"));
const COPPage = lazy(() => import("./pages/COPPage"));
const NetworkPage = lazy(() => import("./pages/NetworkPage"));
const PressReleasesPage = lazy(() => import("./pages/PressReleasesPage"));
const PressReleaseDetailPage = lazy(() => import("./pages/PressReleaseDetailPage"));
const GtfSpotlightPage = lazy(() => import("./pages/GtfSpotlightPage"));
const KaasAnnualReportPage = lazy(() => import("./pages/media/KaasAnnualReportPage"));
const RegisteredCampusesPage = lazy(() => import("./pages/RegisteredCampusesPage"));
const YouthAmbassadorsPage = lazy(() => import("./pages/YouthAmbassadorsPage"));
const RegionalHubsPage = lazy(() => import("./pages/RegionalHubsPage"));
const SuccessStoriesPage = lazy(() => import("./pages/SuccessStoriesPage"));
const CareersPage = lazy(() => import("./pages/Careers"));
const GreenCareersPage = lazy(() => import("./pages/GreenCareers"));
const LatestUpdates = lazy(() => import("./pages/LatestUpdates"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const PartnerWithUs = lazy(() => import("./pages/PartnerWithUs"));
const DonateSupport = lazy(() => import("./pages/DonateSupport"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Initiatives Pages
const InitiativesOverview = lazy(() => import("./pages/InitiativesOverview"));
const SCCNPage = lazy(() => import("./pages/SCCN"));
const SCCN = lazy(() => import("./pages/initiatives/SCCN"));
const SCCNDetail = lazy(() => import("./pages/initiatives/SCCNDetail"));
const NetZero = lazy(() => import("./pages/initiatives/NetZero"));
const NetZeroDetail = lazy(() => import("./pages/initiatives/NetZeroDetail"));
const U75Consortium = lazy(() => import("./pages/initiatives/U75Consortium"));
const U75Detail = lazy(() => import("./pages/initiatives/U75Detail"));
const UDCs = lazy(() => import("./pages/initiatives/UDCs"));
const UDCsDetail = lazy(() => import("./pages/initiatives/UDCsDetail"));
const VirtualCOP = lazy(() => import("./pages/initiatives/VirtualCOP"));
const VirtualCOPDetail = lazy(() => import("./pages/initiatives/VirtualCOPDetail"));


class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-2xl text-center">
            <h2 className="text-2xl font-medium text-red-600 mb-4">
              Something went wrong
            </h2>
            <p className="text-gray-700 mb-6">
              We're sorry, but an unexpected error occurred. Please try
              refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  // Initialize GSAP
  useEffect(() => {
    gsap.registerPlugin();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <ErrorBoundary>
          <ScrollToTop />
          <div className="App">
            <Navbar />
            <Suspense fallback={null}>
              <PremiumPopup />
            </Suspense>
            <FloatingRegistrationButtons />
            <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/about/founder-directors" element={<FounderDirectors />} />
              <Route path="/about/founder-directors/rajendra-shende" element={<RajendraShende />} />
              <Route path="/about/founder-directors/sandeep-narang" element={<SandeepNarang />} />
              <Route path="/about/associates" element={<OurAssociatesPage />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/register" element={<Register />} />
              
              {/* Protected Dashboard Routes */}
              <Route 
                path="/university/dashboard" 
                element={
                  <ProtectedRoute requireUserType="university">
                    <UniversityDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/student/dashboard" 
                element={
                  <ProtectedRoute requireUserType="student">
                    <StudentDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Protected Profile Routes */}
              <Route 
                path="/university/profile" 
                element={
                  <ProtectedRoute requireUserType="university">
                    <UniversityProfile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/student/profile" 
                element={
                  <ProtectedRoute requireUserType="student">
                    <StudentProfile />
                  </ProtectedRoute>
                } 
              />
              
              <Route
                path="/register/university"
                element={<UniversityRegister apiService={apiService} />}
              />
              <Route
                path="/register/student"
                element={<StudentRegister apiService={apiService} />}
              />
              <Route
                path="/blogs"
                element={<BlogsPage apiService={apiService} />}
              />
              <Route
                path="/blogs/:slug"
                element={<BlogDetailPage apiService={apiService} />}
              />
              <Route
                path="/simply-smart"
                element={<SimplySmartPage apiService={apiService} />}
              />
              <Route
                path="/simply-smart/:slug"
                element={<NewsletterDetailPage type="simply-smart" apiService={apiService} />}
              />
              <Route
                path="/globally-smart"
                element={<GloballySmartPage apiService={apiService} />}
              />
              <Route
                path="/globally-smart/:slug"
                element={<NewsletterDetailPage type="globally-smart" apiService={apiService} />}
              />
              <Route
                path="/contact"
                element={<ContactPage apiService={apiService} />}
              />
              <Route
                path="/events"
                element={<EventsPage apiService={apiService} />}
              />
              <Route
                path="/events/calendar"
                element={<EventsPage apiService={apiService} />}
              />
              <Route
                path="/events/past"
                element={<PastEventsPage />}
              />
              <Route
                path="/events/:slug"
                element={<EventDetailPage apiService={apiService} />}
              />

              <Route
                path="/media/cop"
                element={<COPPage apiService={apiService} />}
              />
              <Route
                path="/network"
                element={<NetworkPage apiService={apiService} />}
              />
              <Route path="/u75/resources" element={<ResourcesPage />} />

              <Route
                path="/careers"
                element={<CareersPage apiService={apiService} />}
              />
              <Route
                path="/green-careers"
                element={<GreenCareersPage />}
              />
              <Route
                path="/latest-updates"
                element={<LatestUpdates apiService={apiService} />}
              />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/partner-with-us" element={<PartnerWithUs />} />
              <Route path="/donate-support" element={<DonateSupport />} />

              {/* Resources Route */}
              <Route path="/resources" element={<ResourcesPage />} />
              
              {/* Individual Resources Routes - Currently disabled, merged into single Resources page */}
              {/* <Route path="/resources/youth-toolkits" element={<YouthToolkits />} /> */}
              {/* <Route path="/resources/campus-toolkits" element={<CampusToolkits />} /> */}
              {/* <Route path="/resources/policy-papers" element={<PolicyPapers />} /> */}
              {/* <Route path="/resources/handbooks" element={<Handbooks />} /> */}
              {/* <Route path="/resources/downloads" element={<Downloads />} /> */}

              <Route path="/student-registration" element={<StudentRegister apiService={apiService} />} />
              <Route path="/campus-registration" element={<UniversityRegister apiService={apiService} />} />

              {/* SCCN Direct Route */}
              <Route path="/sccn" element={<SCCNPage />} />
              <Route path="/sccn/mentors" element={<SCCNMentorsPage />} />
              <Route path="/sccn/advisors" element={<SCCNAdvisorsPage />} />
              <Route path="/sccn/experts" element={<SCCNExpertsPage />} />
              <Route path="/sccn/team" element={<SCCNTeamPage />} />

              {/* Initiatives Routes */}
              <Route path="/initiatives" element={<InitiativesOverview />} />
              <Route path="/initiatives/sccn" element={<SCCN />} />
              <Route path="/initiatives/sccn/detail" element={<SCCNDetail />} />
              <Route path="/initiatives/net-zero" element={<NetZero />} />
              <Route path="/initiatives/net-zero/detail" element={<NetZeroDetail />} />
              <Route path="/initiatives/u75-consortium" element={<U75Consortium />} />
              <Route path="/initiatives/u75-consortium/detail" element={<U75Detail />} />
              <Route path="/initiatives/udcs" element={<UDCs />} />
              <Route path="/initiatives/udcs/detail" element={<UDCsDetail />} />
              <Route path="/initiatives/virtual-cop" element={<VirtualCOP />} />
              <Route path="/initiatives/virtual-cop/detail" element={<VirtualCOPDetail />} />

              {/* Media & Insights Routes */}
              <Route path="/media/press-releases" element={<PressReleasesPage />} />
              <Route path="/media/press-releases/:slug" element={<PressReleaseDetailPage />} />
              <Route path="/media/gtf-spotlight" element={<GtfSpotlightPage />} />
              <Route path="/media/kaas-annual-report-card" element={<KaasAnnualReportPage />} />

              {/* Action Network Routes */}
              <Route path="/action-network/campuses" element={<RegisteredCampusesPage />} />
              <Route path="/action-network/regional-hubs" element={<RegionalHubsPage />} />
              <Route path="/action-network/success-stories" element={<SuccessStoriesPage />} />
              <Route path="/action-network/youth-ambassadors" element={<YouthAmbassadorsPage />} />

              {/* 404 Catch-all Route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
          </div>
        </ErrorBoundary>
      </AuthProvider>
    </Router>
  );
}

export default App;